import React, { useState, useEffect } from 'react';
import {
  Toolbar,
  AppBar,
  Button,
  MenuItem,
  Fade,
  FormControl,
  InputLabel,
  Theme,
  Grid,
  Paper,
  ClickAwayListener,
  Badge,
  useTheme,
  MenuIcon,
  ArrowDropDown as ArrowDropDownIcon,
  makeStyles,
  createStyles,
} from '@quillbot/design-system';
import { useRouter } from 'next/router';
import QuillLocalStorage from 'core/utils/localStorageClient';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '@Common/models/redux/store';
import { setSidebarPinnned } from '@Common/actions/commonActions';
import AccountMenu from './AccountMenu';

const styles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      position: 'relative',
      padding: 0,
      margin: 0,
    },
    ParaBtn: {
      color: '#fff',
      position: 'relative',
      width: '120px',
      marginTop: '0px !important',
      '& svg': {
        color: '#fff',
      },
    },

    button: {
      display: 'block',
      marginTop: theme.spacing(2),
    },
    formControl: {
      margin: theme.spacing(1),
      minWidth: 120,
    },
    badgePosition: {
      top: '34%',
      right: '14%',
      transform: 'scale(1) translate(50%, -50%)',
      transformOrigin: '100% 0%',
    },
    badgeDimensions: {
      height: '10px',
      width: '10px',
      padding: '0',
      minWidth: '8px',
      borderRadius: '5px',
    },
    badgeColor: {
      backgroundColor: '#E86060',
    },
    dropdownLabel: {
      maxWidth: 'initial',
    },
  })
);
const inputLabelText: { [key: string]: string } = {
  '/': 'Paraphraser',
  '/summarize': 'Summarizer',
  '/grammar-check': 'Grammar Checker',
  '/plagiarism-checker': 'Plagiarism Checker',
  '/citation-generator': 'Citation Generator',
};
function HeaderExt(props: { isExtension: boolean }) {
  const classes = styles();
  const { isExtension } = props;
  const sidebarPinned = useSelector(
    (rootState: RootState) => rootState.common.sidebarPinned
  );
  const router = useRouter();
  const theme = useTheme();
  const dispatch = useDispatch();
  const [open, setOpen] = useState(false);
  const [invisible, setInvisible] = React.useState(true);
  const extensionMetaData = useSelector(
    (state: RootState) => state.common.platform.extensionMetaData
  );
  const isWordExt = extensionMetaData?.type === 'ms-word';

  useEffect(() => {
    if (isExtension) {
      setInvisible(QuillLocalStorage.getInstance().get('visitedGC', '') !== '');
    }
  }, []);

  useEffect(() => {
    if (router.pathname === '/grammar-check') setInvisible(true);
  }, [router.pathname]);

  const handleDrawerButton = () => {
    if (open) {
      setOpen(false);
    }
    dispatch(setSidebarPinnned(!sidebarPinned));
  };
  const closeMenu = () => setOpen(false);
  const toggleOpen = () => {
    if (sidebarPinned) {
      dispatch(setSidebarPinnned(false));
    }
    setOpen(!open);
  };
  const selectClickHandler = (value: string) => {
    closeMenu();
    router.push(value);
  };

  let product = [];

  if (isExtension) {
    product = ['/', '/grammar-check', '/summarize'];
  } else {
    product = [
      '/',
      '/grammar-check',
      '/plagiarism-checker',
      '/summarize',
      '/citation-generator',
    ];
  }

  const showMenuItem = (path: string): string => inputLabelText[path];

  return (
    <ClickAwayListener onClickAway={closeMenu}>
      <div className={classes.root}>
        <AppBar
          position="static"
          color="primary"
          style={{
            boxShadow: 'none',
          }}
        >
          <Grid container>
            <Grid item xs>
              <Toolbar style={{ minHeight: '40px' }}>
                <FormControl onClick={toggleOpen}>
                  <InputLabel
                    focused={false}
                    shrink={false}
                    classes={{
                      root: classes.dropdownLabel,
                    }}
                    style={{
                      position: 'relative',
                      display: isExtension ? 'flex' : 'block',
                      transform: 'none',
                      fontSize: `${isExtension ? '14px' : '17px'}`,
                      color: '#fff',
                      fontWeight: 600,
                      // top: '50%',
                      // marginTop: '-17px',
                      // height: '40px',
                      lineHeight: '40px',
                    }}
                  >
                    {inputLabelText[router.pathname]}

                    {isExtension && (
                      <ArrowDropDownIcon
                        style={{
                          position: 'relative',
                          top: 8,
                          color: 'white',
                          marginLeft: '5px',
                        }}
                      />
                    )}
                  </InputLabel>
                </FormControl>
              </Toolbar>
            </Grid>
            {isExtension ? (
              <Grid item>
                <Toolbar
                  style={{
                    justifyContent: 'flex-end',
                    minHeight: '38px',
                    padding: isWordExt ? '0px 37px 0px 12px' : '0px 12px',
                  }}
                >
                  <AccountMenu />
                  <Badge
                    color="secondary"
                    overlap="circular"
                    badgeContent=" "
                    variant="dot"
                    invisible={invisible}
                    style={{ marginLeft: 12 }}
                    classes={{
                      anchorOriginTopRightCircular: classes.badgePosition,
                      dot: classes.badgeDimensions,
                      colorSecondary: classes.badgeColor,
                    }}
                  >
                    <Button
                      onClick={handleDrawerButton}
                      style={{ minWidth: 'auto', padding: '6px 0px' }}
                    >
                      <MenuIcon style={{ color: '#fff' }} />
                    </Button>
                  </Badge>
                </Toolbar>
              </Grid>
            ) : (
              <Grid item onClick={toggleOpen}>
                <Toolbar
                  style={{
                    justifyContent: 'flex-end',
                    minHeight: '38px',
                    padding: '7px 12px',
                  }}
                >
                  <ArrowDropDownIcon
                    style={{ marginTop: 2, color: 'white', marginRight: '5px' }}
                  />
                </Toolbar>
              </Grid>
            )}
          </Grid>
        </AppBar>
        <Fade in={open}>
          <Paper
            elevation={3}
            style={{
              position: 'absolute',
              top: 35,
              right: 0,
              left: 0,
              zIndex: 15,
              borderRadius: '0px',
            }}
          >
            {product.map((path) => (
              <MenuItem
                style={{
                  borderBottom: `1px solid ${theme.palette.divider}`,
                  minHeight: '40px',
                  display: path === router.pathname ? 'none' : 'block',
                }}
                onClick={() => selectClickHandler(path)}
                key={path}
              >
                {showMenuItem(path)}
              </MenuItem>
            ))}
          </Paper>
        </Fade>
      </div>
    </ClickAwayListener>
  );
}

export default HeaderExt;
